<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header">
          <h3 class="mb-0" style="text-transform: capitalize">Chalans List</h3>
        </div>

        <!-- Filter Panel -->
        <div class="flex-shrink-0 px-4 py-2 mb-2 bg-gray-50 rounded-lg border border-gray-200">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <!-- Date Range -->
            <div class="col-span-2 space-y-2">
              <label class="block text-xs font-medium text-gray-600">Date Range</label>
              <div class="flex space-x-2">
                <input
                  v-model="panelFilters.fromDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  v-model="panelFilters.toDate"
                  type="date"
                  class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div class="flex flex-wrap gap-1">
                <button type="button" @click="setDateFilter('currentFY')" :class="dateQuickFilterClass('currentFY')">Current FY</button>
                <button type="button" @click="setDateFilter('prevFY')" :class="dateQuickFilterClass('prevFY')">Prev FY</button>
                <button type="button" @click="setDateFilter('today')" :class="dateQuickFilterClass('today')">Today</button>
                <button type="button" @click="setDateFilter('yesterday')" :class="dateQuickFilterClass('yesterday')">Yesterday</button>
                <button type="button" @click="setDateFilter('thisWeek')" :class="dateQuickFilterClass('thisWeek')">This Week</button>
                <button type="button" @click="setDateFilter('thisMonth')" :class="dateQuickFilterClass('thisMonth')">This Month</button>
                <button type="button" @click="setDateFilter('lastMonth')" :class="dateQuickFilterClass('lastMonth')">Last Month</button>
              </div>
            </div>
            <!-- Chalan Number -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Chalan Number</label>
              <input
                v-model="panelFilters.chalanNumber"
                type="text"
                @keyup.enter="applySingleFilter('chalanNumber')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Customer Name -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Customer Name</label>
              <input
                v-model="panelFilters.customerName"
                type="text"
                @keyup.enter="applySingleFilter('customerName')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- SR Number -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">SR Number</label>
              <input
                v-model="panelFilters.serviceRequestNumber"
                type="text"
                @keyup.enter="applySingleFilter('serviceRequestNumber')"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <!-- Type -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Type</label>
              <select
                v-model="panelFilters.isInward"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Types</option>
                <option value="1">Inward</option>
                <option value="0">Outward</option>
              </select>
            </div>
            <!-- Status -->
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-600">Status</label>
              <select
                v-model="panelFilters.status"
                class="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="dispatched">Dispatched</option>
                <option value="accepted">Accepted</option>
              </select>
            </div>
            <!-- Actions -->
            <div class="space-y-2">
              <label class="block text-xs font-medium text-gray-600">Actions</label>
              <div class="flex space-x-2">
                <button
                  @click="applyFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
                >Apply</button>
                <button
                  @click="clearFilters"
                  class="flex-1 px-3 py-2 text-sm text-white bg-gray-500 rounded-md transition-colors hover:bg-gray-600"
                >Clear</button>
              </div>
            </div>
          </div>

          <!-- Active Filters -->
          <div v-if="hasActiveFilters" class="pt-4 mt-4 border-t border-gray-200">
            <div class="flex flex-wrap gap-2 items-center">
              <span class="text-xs font-medium text-gray-600">Active Filters:</span>
              <span
                v-if="appliedFilters.fromDate || appliedFilters.toDate"
                class="inline-flex items-center px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded-full"
              >
                Date: {{ formatAppliedDateRange() }}
                <button @click="clearDateFilter" class="ml-1 text-blue-600 hover:text-blue-800">×</button>
              </span>
              <span
                v-if="appliedFilters.chalanNumber"
                class="inline-flex items-center px-2 py-1 text-xs text-orange-800 bg-orange-100 rounded-full"
              >
                Chalan: {{ appliedFilters.chalanNumber }}
                <button @click="clearAppliedFilter('chalanNumber')" class="ml-1 text-orange-600 hover:text-orange-800">×</button>
              </span>
              <span
                v-if="appliedFilters.customerName"
                class="inline-flex items-center px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full"
              >
                Customer: {{ appliedFilters.customerName }}
                <button @click="clearAppliedFilter('customerName')" class="ml-1 text-green-600 hover:text-green-800">×</button>
              </span>
              <span
                v-if="appliedFilters.serviceRequestNumber"
                class="inline-flex items-center px-2 py-1 text-xs text-teal-800 bg-teal-100 rounded-full"
              >
                SR No: {{ appliedFilters.serviceRequestNumber }}
                <button @click="clearAppliedFilter('serviceRequestNumber')" class="ml-1 text-teal-600 hover:text-teal-800">×</button>
              </span>
              <span
                v-if="appliedFilters.isInward !== ''"
                class="inline-flex items-center px-2 py-1 text-xs text-indigo-800 bg-indigo-100 rounded-full"
              >
                Type: {{ appliedFilters.isInward === '1' ? 'Inward' : 'Outward' }}
                <button @click="clearAppliedFilter('isInward')" class="ml-1 text-indigo-600 hover:text-indigo-800">×</button>
              </span>
              <span
                v-if="appliedFilters.status"
                class="inline-flex items-center px-2 py-1 text-xs text-red-800 bg-red-100 rounded-full"
              >
                Status: {{ appliedFilters.status }}
                <button @click="clearAppliedFilter('status')" class="ml-1 text-red-600 hover:text-red-800">×</button>
              </span>
            </div>
          </div>
        </div>

        <el-table
          :data="allChalans"
          style="width: 100%"
          header-row-class-name="thead-light"
        >
          <el-table-column label="Date" min-width="90">
            <template slot-scope="scope">{{
              scope.row.updatedAt | dateFormat
            }}</template>
          </el-table-column>
          <el-table-column
            label="Chalan No."
            prop="chalanNumber"
            min-width="90"
          />
          <el-table-column label="Customer" min-width="130">
            <template slot-scope="scope">
              <span
                >{{ scope.row.user.firstName }}
                {{ scope.row.user.lastName }}</span
              >
            </template>
          </el-table-column>
          <el-table-column label="Vendor" min-width="130">
            <template slot-scope="scope">
              <span
                >{{ scope.row.vendor.firstName }}
                {{ scope.row.vendor.lastName }}</span
              >
            </template>
          </el-table-column>
          <el-table-column label="Branch" min-width="110">
            <template slot-scope="scope">
              <span
                v-if="scope.row.branchName"
                class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                :style="branchBadgeStyle(scope.row)"
              >{{ scope.row.branchName }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Type" min-width="100">
            <template slot-scope="scope">{{
              scope.row.isInward | typeFilter
            }}</template>
          </el-table-column>
          <el-table-column label="Service Req. No." min-width="110">
            <template slot-scope="scope">
              <span v-if="scope.row.serviceRequestNumber">{{
                scope.row.serviceRequestNumber
              }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Sale Invoice" min-width="110">
            <template slot-scope="scope">
              <button
                v-if="scope.row.saleId"
                @click="openInvoicePrint(scope.row.saleId)"
                class="text-blue-600 hover:underline text-xs font-medium"
              >View Invoice</button>
              <span v-else class="text-gray-400 text-xs">—</span>
            </template>
          </el-table-column>
          <el-table-column label="Status" min-width="110">
            <template slot-scope="scope">
              <span v-if="scope.row.status">
                <el-tag
                  v-if="scope.row.status.code === 'pending'"
                  class="pending-status"
                  effect="dark"
                  >{{ scope.row.status.label }}</el-tag
                >
                <el-tag
                  v-else-if="scope.row.status.code === 'accepted'"
                  type="success"
                  effect="dark"
                  >{{ scope.row.status.label }}</el-tag
                >
                <el-tag
                  v-else-if="scope.row.status.code === 'dispatched'"
                  class="dispatch-status"
                  effect="dark"
                  >{{ scope.row.status.label }}</el-tag
                >
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="130">
            <template slot-scope="scope">
              <el-dropdown
                slot="dropdown"
                @command="command => handleAction(scope.row, command)"
              >
                <el-button type="primary" size="mini"> Actions </el-button>
                <el-dropdown-menu
                  slot="dropdown"
                  class="dropdown-menu dropdown-menu-arrow show"
                >
                  <el-dropdown-item command="details"
                    >View Details</el-dropdown-item
                  >
                  <el-dropdown-item command="print">Print</el-dropdown-item>
                  <el-dropdown-item command="showChalan"
                    >Send To WhatsApp</el-dropdown-item
                  >
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
        <div class="card-footer">
          <div
            v-if="chalanMeta"
            slot="footer"
            class="flex-wrap col-12 d-flex justify-content-center justify-content-sm-between"
          >
            <div class>
              <p class="card-category">
                Showing {{ chalanMeta.from + 1 }} to {{ chalanMeta.to }} of
                {{ chalanMeta.total }} entries
                <span v-if="selectedRows.length"
                  >&nbsp; &nbsp; {{ selectedRows.length }} rows selected</span
                >
              </p>
            </div>
            <pagination :meta="chalanMeta" @paginate="setPagination" />
          </div>
        </div>
      </div>
    </div>
    <el-dialog
      :visible.sync="showChalanModal"
      title="Chalan Invoice"
      width="50%"
    >
      <chalan-design :data="chalanData" @close="showChalanModal = false" />
    </el-dialog>
    <el-dialog
      @open="onInvoiceDialogOpen"
      @close="onInvoiceDialogClose"
      :visible.sync="showInvoiceModal"
      width="60%"
      title="Invoice"
    >
      <whatsapp-bill-print ref="saleInvoice" />
    </el-dialog>
  </div>
</template>

<script>
import headerComponent from "../Components/HeaderComponent";
import { mapActions, mapState, mapGetters } from "vuex";
import dateFns from "date-fns";
import pagination from "@/components/AmsaPagination";
import { print } from "@/utils/print";

import {
  Select,
  Option,
  DatePicker,
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Dialog,
  Tag,
  Button
} from "element-ui";
import ChalanDesign from "./ChalanDesign.vue";
import WhatsAppBillPrint from "../Pos/WhatsAppBillPrint.vue";

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function calendarMonthRange(offset = 0, refDate = new Date()) {
  const y = refDate.getFullYear();
  const m = refDate.getMonth() + offset;
  return {
    fromDate: formatLocalDate(new Date(y, m, 1)),
    toDate: formatLocalDate(new Date(y, m + 1, 0))
  };
}

function indianFinancialYearRange(period = "current", refDate = new Date()) {
  const y = refDate.getFullYear();
  const month = refDate.getMonth();
  let fyStartYear = month >= 3 ? y : y - 1;
  if (period === "prev") fyStartYear -= 1;
  return {
    fromDate: `${fyStartYear}-04-01`,
    toDate: `${fyStartYear + 1}-03-31`
  };
}

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Tag.name]: Tag,
    [Button.name]: Button,
    pagination,
    "header-component": headerComponent,
    "chalan-design": ChalanDesign,
    "whatsapp-bill-print": WhatsAppBillPrint
  },
  filters: {
    dateFormat(value) {
      return dateFns.format(value, "DD MMM YYYY");
    },
    typeFilter(value) {
      if (value === true) {
        return `Inward`;
      } else {
        return `Outward`;
      }
    }
  },
  data() {
    const defaultFY = indianFinancialYearRange("current");
    return {
      selectedRows: [],
      pageSize: 10,
      showChalanModal: false,
      chalanData: null,
      showInvoiceModal: false,
      panelFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        chalanNumber: "",
        customerName: "",
        serviceRequestNumber: "",
        isInward: "",
        status: ""
      },
      appliedFilters: {
        fromDate: defaultFY.fromDate,
        toDate: defaultFY.toDate,
        chalanNumber: "",
        customerName: "",
        serviceRequestNumber: "",
        isInward: "",
        status: ""
      },
      _skipFilterWatchFetch: false
    };
  },
  computed: {
    ...mapState("serviceRequestChalan", ["allChalans", "chalanMeta"]),
    ...mapState("serviceRequestChalan", ["serviceRequestChalan"]),
    ...mapGetters({
      statusList: "status/getStatusList"
    }),
    hasActiveFilters() {
      return Object.values(this.appliedFilters).some(v => v !== "");
    },
    activeDatePreset() {
      const f = this.panelFilters.fromDate;
      const t = this.panelFilters.toDate;
      if (!f || !t) return null;

      const cur = indianFinancialYearRange("current");
      if (f === cur.fromDate && t === cur.toDate) return "currentFY";

      const prev = indianFinancialYearRange("prev");
      if (f === prev.fromDate && t === prev.toDate) return "prevFY";

      const today = new Date();
      const todayStr = formatLocalDate(today);
      if (f === todayStr && t === todayStr) return "today";

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = formatLocalDate(yesterday);
      if (f === yesterdayStr && t === yesterdayStr) return "yesterday";

      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      if (f === formatLocalDate(startOfWeek) && t === todayStr) return "thisWeek";

      const thisMonth = calendarMonthRange(0, today);
      if (f === thisMonth.fromDate && t === thisMonth.toDate) return "thisMonth";

      const lastMonth = calendarMonthRange(-1, today);
      if (f === lastMonth.fromDate && t === lastMonth.toDate) return "lastMonth";

      return null;
    }
  },
  watch: {
    "panelFilters.fromDate"(newVal) {
      this.appliedFilters.fromDate = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    },
    "panelFilters.toDate"(newVal) {
      this.appliedFilters.toDate = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    },
    "panelFilters.isInward"(newVal) {
      this.appliedFilters.isInward = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    },
    "panelFilters.status"(newVal) {
      this.appliedFilters.status = newVal;
      if (!this._skipFilterWatchFetch) this.fetchChalans(1);
    }
  },
  mounted() {
    this.getStatusList();
    this.fetchChalans();
  },
  methods: {
    ...mapActions({
      getAllChalans: "serviceRequestChalan/getAllChalans",
      getStatusList: "status/getStatusList",
      getChalanDetails: "serviceRequestChalan/getServiceRequestChalanDetails",
      printChalan: "serviceRequestChalan/printChalan",
      getReceiptDataForWhatsapp: "sale/getReceiptDataForWhatsapp"
    }),
    openInvoicePrint(saleId) {
      this.getReceiptDataForWhatsapp(saleId).then(status => {
        if (status === 200) {
          this.showInvoiceModal = true;
        }
      });
    },
    onInvoiceDialogOpen() {
      if (this.$refs.saleInvoice) this.$refs.saleInvoice.fetchData();
    },
    onInvoiceDialogClose() {
      if (this.$refs.saleInvoice) this.$refs.saleInvoice.resetProperty();
    },
    branchBadgeStyle(row) {
      if (!row.branchColor) return { background: '#dbeafe', color: '#1e40af' };
      return { background: row.branchColor, color: '#fff' };
    },
    fetchChalans(page = 1) {
      const query = { page, perPage: this.pageSize };
      if (this.appliedFilters.fromDate) query.fromDate = this.appliedFilters.fromDate;
      if (this.appliedFilters.toDate) query.toDate = this.appliedFilters.toDate;
      if (this.appliedFilters.chalanNumber) query.chalanNumber = this.appliedFilters.chalanNumber;
      if (this.appliedFilters.customerName) query.customerName = this.appliedFilters.customerName;
      if (this.appliedFilters.serviceRequestNumber) query.serviceRequestNumber = this.appliedFilters.serviceRequestNumber;
      if (this.appliedFilters.isInward !== "") query.isInward = this.appliedFilters.isInward;
      if (this.appliedFilters.status) query.status = this.appliedFilters.status;
      this.getAllChalans(query);
    },
    setPagination(data) {
      this.pageSize = data.perPage;
      this.fetchChalans(data.page);
    },
    applyFilters() {
      this.appliedFilters = { ...this.panelFilters };
      this.fetchChalans(1);
    },
    applySingleFilter(filterName) {
      this.appliedFilters[filterName] = this.panelFilters[filterName];
      this.fetchChalans(1);
    },
    clearAppliedFilter(filterName) {
      this.appliedFilters[filterName] = "";
      this.panelFilters[filterName] = "";
      this.fetchChalans(1);
    },
    clearDateFilter() {
      this._skipFilterWatchFetch = true;
      this.panelFilters.fromDate = "";
      this.panelFilters.toDate = "";
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.appliedFilters.fromDate = "";
        this.appliedFilters.toDate = "";
        this.fetchChalans(1);
      });
    },
    clearFilters() {
      this._skipFilterWatchFetch = true;
      const empty = {
        fromDate: "", toDate: "", chalanNumber: "", customerName: "",
        serviceRequestNumber: "", isInward: "", status: ""
      };
      this.panelFilters = { ...empty };
      this.appliedFilters = { ...empty };
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.fetchChalans(1);
      });
    },
    setDateFilter(type) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());

      let range;
      switch (type) {
        case "currentFY": range = indianFinancialYearRange("current"); break;
        case "prevFY": range = indianFinancialYearRange("prev"); break;
        case "today": range = { fromDate: formatLocalDate(today), toDate: formatLocalDate(today) }; break;
        case "yesterday": range = { fromDate: formatLocalDate(yesterday), toDate: formatLocalDate(yesterday) }; break;
        case "thisWeek": range = { fromDate: formatLocalDate(startOfWeek), toDate: formatLocalDate(today) }; break;
        case "thisMonth": range = calendarMonthRange(0, today); break;
        case "lastMonth": range = calendarMonthRange(-1, today); break;
        default: return;
      }
      this._skipFilterWatchFetch = true;
      this.panelFilters.fromDate = range.fromDate;
      this.panelFilters.toDate = range.toDate;
      this.$nextTick(() => {
        this._skipFilterWatchFetch = false;
        this.appliedFilters.fromDate = range.fromDate;
        this.appliedFilters.toDate = range.toDate;
        this.fetchChalans(1);
      });
    },
    dateQuickFilterClass(preset) {
      const selected = this.activeDatePreset === preset;
      return [
        "px-2 py-1 text-xs rounded transition-colors",
        selected
          ? "text-blue-700 bg-blue-100 hover:bg-blue-200"
          : "text-gray-700 bg-gray-100 hover:bg-gray-200"
      ];
    },
    formatAppliedDateRange() {
      const f = this.appliedFilters.fromDate;
      const t = this.appliedFilters.toDate;
      if (f && t) {
        if (f === t) return new Date(f).toLocaleDateString("en-IN");
        return `${new Date(f).toLocaleDateString("en-IN")} - ${new Date(t).toLocaleDateString("en-IN")}`;
      }
      if (f) return `From ${new Date(f).toLocaleDateString("en-IN")}`;
      if (t) return `Until ${new Date(t).toLocaleDateString("en-IN")}`;
      return "";
    },
    async handleAction(row, command) {
      if (command === "details" || command === "showChalan") {
        this.chalanData = await this.getChalanDetails(
          row.serviceRequestChalanId
        );
        this.showChalanModal = true;
      } else if (command === "print") {
        await this.handlePrint(row);
      }
    },

    async handlePrint(row) {
      try {
        const chalanData = await this.getChalanDetails(
          row.serviceRequestChalanId
        );

        const products = chalanData.chalanItems.map(i => {
          return {
            name: i.name,
            quantity: i.quantity,
            price: i.price,
            total: i.amount
          };
        });

        const subTotal = parseFloat(chalanData.total.toFixed(2)) || 0;

        const printData = {
          chalanItems: products || [],
          users: chalanData.users || {},
          vendor: chalanData.vendor || {},
          chalanNumber:
            chalanData.chalanNumber || chalanData.serviceRequestChalanId,
          isInward: chalanData.isInward || false,
          total: parseFloat(chalanData.total.toFixed(2)) || 0,
          subTotal: subTotal,
          grandTotal: chalanData.grandTotal || chalanData.total || 0,
          roundedOff:
            parseFloat(chalanData.grandTotal - subTotal).toFixed(2) || 0,
          updatedAt: chalanData.updatedAt || new Date()
        };

        print(printData, "chalan");

        this.$notify({
          message: "Chalan sent to printer!",
          type: "success",
          title: "Success"
        });
      } catch (error) {
        console.error("Error printing chalan:", error);
        this.$notify({
          message: "Error printing chalan. Please try again.",
          type: "danger",
          title: "Error"
        });
      }
    }
  }
};
</script>

<style>
.pending-status {
  background-color: #e72626;
  border-color: #e72626;
  color: #fff;
}
.dispatch-status {
  background-color: #f57316eb;
  border-color: #f57316eb;
  color: #fff;
}
</style>
